import {
  APIComponentAttributes,
  APIComponentType,
  APIDiffData,
  APIEntityType,
  APIFieldAttributes,
  APIModelAttributes,
  APIReferenceAttributes,
  APIReferenceType,
  APIScopeData,
  APITagAttributes,
  ArdoqId,
  PROVENANCE_FIELD_KEYS,
  ScopeDataCollection,
} from '@ardoq/api-types';
import { GlobalReferenceTypeId } from '@ardoq/data-model';
import { omit, pickBy } from 'lodash';
import { toByIdDictionary } from '@ardoq/common-helpers';
import { toId, toIdSet } from '@ardoq/core';

export const diffSourceToTarget = ({
  sourceData,
  targetData,
  dynamicFilter = () => true,
  exposeAsDeletedFilter = () => true,
  exposeAsCreatedFilter = () => true,
  ignoredProperties,
}: {
  sourceData: APIScopeData;
  targetData: APIScopeData;
  dynamicFilter?: DynamicFilter;
  exposeAsDeletedFilter?: (id: string) => boolean;
  exposeAsCreatedFilter?: (id: string) => boolean;
  ignoredProperties?: Record<APIEntityType, Set<string>>;
}) => {
  const {
    componentTypes,
    sourceComponentTypesWithMetaInfo,
    targetComponentTypesWithMetaInfo,
  } = getComponentTypesDiffData(sourceData, targetData);

  const {
    referenceTypes,
    sourceReferenceTypesWithMetaInfo,
    targetReferenceTypesWithMetaInfo,
  } = getReferenceTypesDiffData(sourceData, targetData);

  const diffData = {
    [ScopeDataCollection.COMPONENTS]: getEntityDiff<
      APIScopeData[ScopeDataCollection.COMPONENTS][0]
    >(
      sourceData[ScopeDataCollection.COMPONENTS],
      targetData[ScopeDataCollection.COMPONENTS],
      ignoredProperties?.[APIEntityType.COMPONENT],
      dynamicFilter,
      exposeAsDeletedFilter,
      exposeAsCreatedFilter
    ) as APIDiffData<APIComponentAttributes>,

    [ScopeDataCollection.REFERENCES]: getEntityDiff<
      APIScopeData[ScopeDataCollection.REFERENCES][0]
    >(
      sourceData[ScopeDataCollection.REFERENCES],
      targetData[ScopeDataCollection.REFERENCES],
      ignoredProperties?.[APIEntityType.REFERENCE],
      dynamicFilter,
      exposeAsDeletedFilter
    ) as APIDiffData<APIReferenceAttributes>,

    [ScopeDataCollection.FIELDS]: getEntityDiff<
      APIScopeData[ScopeDataCollection.FIELDS][0]
    >(
      sourceData[ScopeDataCollection.FIELDS],
      targetData[ScopeDataCollection.FIELDS],
      ignoredProperties?.[APIEntityType.FIELD]
    ) as APIDiffData<APIFieldAttributes>,

    [ScopeDataCollection.TAGS]: {
      ...filterTagUpdates(
        getEntityDiff<APIScopeData[ScopeDataCollection.TAGS][0]>(
          sourceData[ScopeDataCollection.TAGS],
          targetData[ScopeDataCollection.TAGS],
          ignoredProperties?.[APIEntityType.TAG]
        )
      ),
      merge: getTagIdsWithDiffOnEntities({
        sourceTags: sourceData[ScopeDataCollection.TAGS],
        targetTags: targetData[ScopeDataCollection.TAGS],
        sourceComponentIds: toIdSet(sourceData[ScopeDataCollection.COMPONENTS]),
        targetComponentIds: toIdSet(targetData[ScopeDataCollection.COMPONENTS]),
        sourceReferenceIds: toIdSet(sourceData[ScopeDataCollection.REFERENCES]),
        targetReferenceIds: toIdSet(targetData[ScopeDataCollection.REFERENCES]),
      }),
    } as {
      update: Record<string, Partial<APITagAttributes>>;
      create: Record<string, APITagAttributes>;
      delete: Record<string, APITagAttributes>;
      merge: ArdoqId[];
    },

    [ScopeDataCollection.MODELS]: getEntityDiff<
      APIScopeData[ScopeDataCollection.MODELS][0]
    >(
      sourceData[ScopeDataCollection.MODELS],
      targetData[ScopeDataCollection.MODELS],
      ignoredProperties?.[APIEntityType.MODEL]
    ) as APIDiffData<APIModelAttributes>,

    [APIEntityType.COMPONENT_TYPE]: componentTypes,
    [APIEntityType.REFERENCE_TYPE]: referenceTypes,
  };
  return {
    diffData,
    sourceMetaData: {
      componentTypes: sourceComponentTypesWithMetaInfo,
      referenceTypes: sourceReferenceTypesWithMetaInfo,
    },
    targetMetaData: {
      componentTypes: targetComponentTypesWithMetaInfo,
      referenceTypes: targetReferenceTypesWithMetaInfo,
    },
  };
};

export type FlatComponentType = FlatType<Omit<APIComponentType, 'children'>>;

const commonIgnoredProperties = [
  '_id',
  '_order',
  '_version',
  'ardoq',
  'created-by-email',
  'created-by-name',
  'created',
  'created-by',
  'createdBy',
  'createdByEmail',
  'createdByName',
  'last-modified-by',
  'last-updated',
  'lastUpdated',
  'lastModifiedBy',
  'lastModifiedByEmail',
  'lastModifiedByName',
  'origin',
  'ardoq-persistent',
  ...PROVENANCE_FIELD_KEYS,
];

export const ignoredProperties: Record<APIEntityType, Set<string>> = {
  [APIEntityType.COMPONENT]: new Set([
    ...commonIgnoredProperties,
    'children',
    'model',
    'order',
    'type', // component has both type and typeId
  ]),
  [APIEntityType.REFERENCE]: new Set([
    ...commonIgnoredProperties,
    'model',
    'order',
  ]),
  [APIEntityType.FIELD]: new Set([
    ...commonIgnoredProperties,
    'calculatedFieldExtras',
    'calculatedFieldSettings',
    'dateTimeFields',
  ]),
  [APIEntityType.TAG]: new Set([...commonIgnoredProperties]),
  [APIEntityType.MODEL]: new Set([
    ...commonIgnoredProperties,
    'referenceTypes',
    'root',
    'maxReferenceTypeKey',
    'workspaces',
  ]),
  [APIEntityType.PERMISSION]: new Set([]),
  [APIEntityType.COMPONENT_TYPE]: new Set([
    'parent',
    'returnsValue',
    'id',
    'index',
    'level',
  ]),
  [APIEntityType.REFERENCE_TYPE]: new Set([
    'returnsValue',
    'id',
    'svgStyle',
    'index',
  ]),
  [APIEntityType.WORKSPACE]: new Set([]),
  [APIEntityType.WORKSPACE_FOLDER]: new Set([]),
  [APIEntityType.ATTACHMENT]: new Set([]),
  [APIEntityType.REPORT]: new Set([]),
  [APIEntityType.DASHBOARD]: new Set([]),
  [APIEntityType.TRAVERSAL]: new Set([]),
  [APIEntityType.SLIDE]: new Set([]),
  [APIEntityType.PRESENTATION]: new Set([]),
  [APIEntityType.CHANGE_REQUEST]: new Set([]),
  [APIEntityType.SCENARIO]: new Set([]),
  [APIEntityType.DOCUMENT_ARCHIVE_FOLDER]: new Set([]),
  [APIEntityType.CALL_TO_ACTION]: new Set([]),
  [APIEntityType.GROUP]: new Set([]),
  [APIEntityType.PERSPECTIVE]: new Set([]),
};

const simpleTypes = new Set([
  'string',
  'number',
  'boolean',
  'undefined',
  'bigint',
  'symbol',
  'function',
]);

export type Diff = {
  [key: string]: Diff | boolean;
};

type DynamicFilter = (srcObj: any, targetObj: any, key: string) => boolean;

const isNullOrUndefined = (value: any) => value === null || value === undefined;

export const getDiff = (
  srcObj: any,
  targetObj: any,
  ignoredProperties = new Set<string>(),
  dynamicFilter: DynamicFilter = () => true
) => {
  const diff: Diff = Array.from(
    new Set([...Object.keys(srcObj || {}), ...Object.keys(targetObj || {})])
  )
    .filter(
      key =>
        !ignoredProperties.has(key) && dynamicFilter(srcObj, targetObj, key)
    )
    .reduce((diff, key) => {
      const srcValue = srcObj[key];
      const targetValue = targetObj[key];
      const typeofSrcValue = typeof srcValue;
      const typeofTargetValue = typeof targetValue;
      if (
        simpleTypes.has(typeofSrcValue) ||
        simpleTypes.has(typeofTargetValue) ||
        srcValue === null ||
        targetValue === null
      ) {
        // NaN === NaN is false, we don't handle that.
        return srcObj[key] === targetObj[key] ||
          (isNullOrUndefined(srcObj[key]) && isNullOrUndefined(targetObj[key]))
          ? diff
          : { ...diff, [key]: true };
      }
      const nestedDiff = getDiff(
        srcObj[key],
        targetObj[key],
        ignoredProperties,
        dynamicFilter
      );
      return nestedDiff ? { ...diff, [key]: nestedDiff } : diff;
    }, {});
  return Object.keys(diff).length === 0 ? null : diff;
};

/**
 * Shallowly diff two objects, returning a set of changed keys.
 * @returns keys Set<string>
 */
export const getShallowDiff = <KeysA extends string, KeysB extends string>(
  objA: Partial<Record<KeysA, unknown>>,
  objB: Partial<Record<KeysB, unknown>>
): Set<string> => {
  const keysA = Object.keys(objA) as KeysA[];
  const keysB = Object.keys(objB) as KeysB[];

  const changedKeys = [...keysA, ...keysB].filter(key => {
    const keyInA = key in objA;
    const keyInB = key in objB;

    const isExclusiveKey = (keyInA && !keyInB) || (!keyInA && keyInB);

    const isSharedKeyWithDifferentValue =
      keyInA && keyInB && !Object.is(objA[key as KeysA], objB[key as KeysB]);

    return isExclusiveKey || isSharedKeyWithDifferentValue;
  });

  return new Set(changedKeys);
};

const getEntityDiff = <
  APIAttributeType extends {
    _id: ArdoqId;
  },
>(
  sourceEntities: APIAttributeType[],
  targetEntities: APIAttributeType[],
  ignoredProperties: Set<string> = new Set(),
  dynamicFilter: DynamicFilter = () => true,
  exposeAsDeletedFilter: (id: string) => boolean = () => true,
  exposeAsCreatedFilter: (id: string) => boolean = () => true
) => {
  const sourceEntitiesById = toByIdDictionary(sourceEntities);
  const targetEntitiesById = toByIdDictionary(targetEntities);
  return getEntityDiffWithByIdRecords(
    sourceEntitiesById,
    targetEntitiesById,
    ignoredProperties,
    dynamicFilter,
    exposeAsDeletedFilter,
    exposeAsCreatedFilter
  );
};

const getEntityDiffWithByIdRecords = <T extends Record<string, unknown>>(
  sourceEntitiesById: Record<string, T>,
  targetEntitiesById: Record<string, T>,
  ignoredProperties: Set<string>,
  dynamicFilter: DynamicFilter = () => true,
  exposeAsDeletedFilter: (id: string) => boolean = () => true,
  exposeAsCreatedFilter: (id: string) => boolean = () => true
): {
  create: Record<string, Partial<T>>;
  delete: Record<string, Partial<T>>;
  update: Record<string, Partial<T>>;
} => {
  const sourceIds = new Set(Object.keys(sourceEntitiesById));
  const targetIds = new Set(Object.keys(targetEntitiesById));
  const diff = getDiff(
    sourceEntitiesById,
    targetEntitiesById,
    ignoredProperties,
    dynamicFilter
  );
  if (diff === null) {
    return { create: {}, delete: {}, update: {} };
  }
  const preserveAlways = new Set(['_id', 'id']);
  const { create, _delete, update } = Object.keys(diff).reduce(
    ({ create, _delete, update }, id) => {
      if (!targetIds.has(id)) {
        return {
          create: exposeAsCreatedFilter(id)
            ? {
                ...create,
                [id]: pickBy<T>(
                  sourceEntitiesById[id],
                  (_value, key) =>
                    !ignoredProperties.has(key) || preserveAlways.has(key)
                ),
              }
            : create,
          _delete,
          update,
        };
      }
      if (!sourceIds.has(id)) {
        return {
          create,
          _delete: exposeAsDeletedFilter(id)
            ? {
                ..._delete,
                [id]: pickBy(
                  targetEntitiesById[id],
                  (_value, key) =>
                    !ignoredProperties.has(key) || preserveAlways.has(key)
                ),
              }
            : _delete,
          update,
        };
      }
      return {
        create,
        _delete,
        update: { ...update, [id]: diff[id] },
      };
    },
    {
      create: {},
      _delete: {},
      update: {},
    }
  );
  return {
    create,
    delete: _delete,
    update,
  };
};

const getIsTaggedEntityInScope =
  (sourceEntityIds: Set<ArdoqId>, targetEntityIds: Set<ArdoqId>) =>
  (entityId: ArdoqId) =>
    sourceEntityIds.has(entityId) && targetEntityIds.has(entityId);

const isEqual = (a: Array<ArdoqId>, b: Array<ArdoqId>) => {
  const aSet = new Set(a);
  const bSet = new Set(b);
  const abSet = new Set([...a, ...b]);
  return aSet.size === bSet.size && aSet.size === abSet.size;
};

const getTagIdsWithDiffOnEntities = ({
  sourceTags,
  targetTags,
  sourceComponentIds,
  targetComponentIds,
  targetReferenceIds,
  sourceReferenceIds,
}: {
  sourceTags: APITagAttributes[];
  targetTags: APITagAttributes[];
  sourceComponentIds: Set<ArdoqId>;
  targetComponentIds: Set<ArdoqId>;
  sourceReferenceIds: Set<ArdoqId>;
  targetReferenceIds: Set<ArdoqId>;
}) => {
  const targetTagsMap = new Map(targetTags.map(target => [target._id, target]));
  return sourceTags
    .filter(sourceTag => {
      const targetTag = targetTagsMap.get(sourceTag._id);
      if (!targetTag) return false;
      return (
        !isEqual(
          sourceTag.components.filter(
            getIsTaggedEntityInScope(sourceComponentIds, targetComponentIds)
          ),
          targetTag.components.filter(
            getIsTaggedEntityInScope(sourceComponentIds, targetComponentIds)
          )
        ) ||
        !isEqual(
          sourceTag.references.filter(
            getIsTaggedEntityInScope(sourceReferenceIds, targetReferenceIds)
          ),
          targetTag.references.filter(
            getIsTaggedEntityInScope(sourceReferenceIds, targetReferenceIds)
          )
        )
      );
    })
    .map(toId);
};

const filterTagUpdates = <
  T extends { update: Record<string, Partial<APITagAttributes>> },
>(
  tags: T
) => ({
  // filters tags that only have differences on components or references, this is handled in the merge
  ...tags,
  update: Object.fromEntries(
    Object.entries(tags.update ?? {}).filter(
      ([, updates]) => updates.name || updates.description
    )
  ),
});

type FlatType<DataType> = {
  modelId: ArdoqId;
  id: ArdoqId;
  path: string[];
  data: DataType;
};

const getComponentTypes = (
  parentType: Record<ArdoqId, APIComponentType>,
  modelId: ArdoqId,
  path: string[]
): Record<string, FlatComponentType> => {
  return Object.values(parentType).reduce(
    (componentTypes, componentType) => {
      const id = componentType.id;
      const uniqueId = `${modelId}-${id}`;
      return {
        ...componentTypes,
        ...getComponentTypes(componentType.children, modelId, [
          ...path,
          id,
          'children',
        ]),
        [uniqueId]: {
          modelId,
          id,
          path: path.slice(),
          data: omit(componentType, ['children']),
        },
      };
    },
    {} as Record<string, FlatComponentType>
  );
};

const getTypesByIdsAndTypesWithMetaInfoByIds = (
  models: APIModelAttributes[]
): [
  Record<string, Omit<APIComponentType, 'children'>>,
  Record<string, FlatComponentType>,
] => {
  const componentTypesWithMetaInfo = models.reduce(
    (componentTypes, model) => ({
      ...componentTypes,
      ...getComponentTypes(model.root, model._id, ['root']),
    }),
    {} as Record<string, FlatComponentType>
  );
  const componentTypes = Object.fromEntries(
    Object.entries(componentTypesWithMetaInfo).map(([id, { data }]) => [
      id,
      data,
    ])
  );
  return [componentTypes, componentTypesWithMetaInfo];
};

const getComponentTypesDiffData = (
  sourceData: APIScopeData,
  targetData: APIScopeData
) => {
  const [sourceComponentTypes, sourceComponentTypesWithMetaInfo] =
    getTypesByIdsAndTypesWithMetaInfoByIds(sourceData.models);

  const [targetComponentTypes, targetComponentTypesWithMetaInfo] =
    getTypesByIdsAndTypesWithMetaInfoByIds(targetData.models);

  const componentTypes = getEntityDiffWithByIdRecords(
    sourceComponentTypes,
    targetComponentTypes,
    ignoredProperties[APIEntityType.COMPONENT_TYPE]
  );

  return {
    componentTypes,
    sourceComponentTypesWithMetaInfo,
    targetComponentTypesWithMetaInfo,
  };
};

type FlatReferenceType = FlatType<APIReferenceType>;

const getReferenceTypes = (
  parentType: Record<GlobalReferenceTypeId, APIReferenceType>,
  modelId: ArdoqId,
  path: string[]
): Record<string, FlatReferenceType> => {
  return Object.fromEntries(
    Object.values(parentType).map(referenceType => {
      const id = referenceType.id;
      const uniqueId = `${modelId}-${id}`;
      return [
        uniqueId,
        {
          modelId,
          id: String(id),
          path: path.slice(),
          data: referenceType,
        },
      ];
    })
  );
};

const getReferenceTypesByIdsAndTypesWithMetaInfoByIds = (
  models: APIModelAttributes[]
): [Record<string, APIReferenceType>, Record<string, FlatReferenceType>] => {
  const referenceTypesWithMetaInfo = models.reduce(
    (referenceTypes, model) => ({
      ...referenceTypes,
      ...getReferenceTypes(model.referenceTypes, model._id, ['referenceTypes']),
    }),
    {} as Record<string, FlatReferenceType>
  );
  const referenceTypes = Object.fromEntries(
    Object.entries(referenceTypesWithMetaInfo).map(([id, { data }]) => [
      id,
      data,
    ])
  );
  return [referenceTypes, referenceTypesWithMetaInfo];
};

const getReferenceTypesDiffData = (
  sourceData: APIScopeData,
  targetData: APIScopeData
) => {
  const [sourceReferenceTypes, sourceReferenceTypesWithMetaInfo] =
    getReferenceTypesByIdsAndTypesWithMetaInfoByIds(sourceData.models);

  const [targetReferenceTypes, targetReferenceTypesWithMetaInfo] =
    getReferenceTypesByIdsAndTypesWithMetaInfoByIds(targetData.models);

  const referenceTypes = getEntityDiffWithByIdRecords(
    sourceReferenceTypes,
    targetReferenceTypes,
    ignoredProperties[APIEntityType.REFERENCE_TYPE]
  );

  return {
    referenceTypes,
    sourceReferenceTypesWithMetaInfo,
    targetReferenceTypesWithMetaInfo,
  };
};
